import { hoursOfValue, validateAvailability, AVAILABILITY_MESSAGES, type AvailabilityDraft, type AvailabilityError, type DateKey, type MonthKey } from '@kff/engine';

/** Form state for the availability form (strings while typing). */
export interface FormState {
  unit: 'percent' | 'hours';
  weeks: Record<DateKey, string>;
  weekendOn: boolean;
  weekend: Record<DateKey, string>;
}

export interface FormModel {
  month: MonthKey;
  weeks: DateKey[];
  weekendDays: { week: DateKey; sat: DateKey; sun: DateKey }[];
  weeklyBaseH: number;
  dueDay: DateKey;
  submission: { unit: 'percent' | 'hours'; weeks: Record<string, number>; weekend: Record<string, number>; submittedAt: string } | null;
  lastMonth: { unit: 'percent' | 'hours' } | null;
  name: string;
}

const str = (v: number | undefined) => (v === undefined || v === null ? '' : String(v));

export function initialState(m: FormModel): FormState {
  const s = m.submission;
  if (!s) return { unit: m.lastMonth?.unit ?? 'percent', weeks: {}, weekendOn: false, weekend: {} };
  return {
    unit: s.unit,
    weeks: Object.fromEntries(m.weeks.map((w) => [w, str(s.weeks[w])])),
    weekendOn: Object.keys(s.weekend).length > 0,
    weekend: Object.fromEntries(Object.entries(s.weekend).map(([k, v]) => [k, String(v)]))
  };
}

export function fromDraft(d: AvailabilityDraft): FormState {
  const weekend = Object.fromEntries(Object.entries(d.weekend ?? {}).map(([k, v]) => [k, String(v ?? '')]));
  return { unit: d.unit, weeks: Object.fromEntries(Object.entries(d.weeks).map(([k, v]) => [k, String(v ?? '')])), weekendOn: Object.keys(weekend).length > 0, weekend };
}

export function toDraft(month: MonthKey, f: FormState): AvailabilityDraft {
  return { month, unit: f.unit, weeks: { ...f.weeks }, weekend: f.weekendOn ? { ...f.weekend } : {} };
}

/** "= 32 h" next to a percent field. */
export function conversion(f: FormState, week: DateKey, base: number): string {
  const v = f.weeks[week];
  if (f.unit !== 'percent' || v === undefined || v.trim() === '' || Number.isNaN(Number(v.replace(',', '.')))) return '';
  return `= ${hoursOfValue('percent', Number(v.replace(',', '.')), base)} h`;
}

/** Client-side check with the same rules as the API (the API checks again). */
export function check(personId: string, month: MonthKey, f: FormState, lang: 'sv' | 'en'): { errors: (AvailabilityError & { message: string })[] } {
  const r = validateAvailability(personId, toDraft(month, f));
  return { errors: r.ok ? [] : r.errors.map((e) => ({ ...e, message: AVAILABILITY_MESSAGES[lang][e.code] })) };
}
